import { useMutation } from '@apollo/client';
import type { UpdateCustomDatasourceMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateCustomDatasourceDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateCustomDatasourceTRPCInput } from './useUpdateCustomDatasourceTRPC';
import { useUpdateCustomDatasourceTRPC } from './useUpdateCustomDatasourceTRPC';

export const useUpdateCustomDatasource = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation — evict the two custom_datasource query families so the
  // cache-first `useGetCustomDatasourceById` read refetches after a save.
  // Without this the edit form re-reads a stale cache on in-app navigation and
  // the just-saved filters/fields appear reverted (RSP-6650). Mirrors the
  // delete hook and the tRPC twin's invalidateQueries.
  const [updateCustomDatasourceGraphQL, graphqlState] = useMutation(
    UpdateCustomDatasourceDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_datasource');
        evictField(cache, 'custom_datasource_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateCustomDatasourceTRPC();

  const updateCustomDatasource = async (
    variables: UpdateCustomDatasourceTRPCInput
  ): Promise<UpdateCustomDatasourceMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateCustomDatasource(variables);
    }

    const result = await updateCustomDatasourceGraphQL({
      variables: {
        Id: variables.Id,
        Data: {
          Title: variables.Title,
          Datasources: variables.Datasources,
          Fields: variables.Fields,
          Filters: variables.Filters,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to update custom datasource');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateCustomDatasource,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateCustomDatasource,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
